import { type IStyles } from '@wix/tpa-style-webpack-plugin/dist/runtime/types';
import React, { useContext, createContext, type ReactNode } from 'react';

import { type Breakpoint } from '../constants';
import {
  type UserData,
  type Currency,
  type PaymentSettingsNavigationConfig,
} from '../types';

export interface Config {
  language: string;
  regionalFormat: string;
  accountLanguage: string;
  timezone?: string;
  instance?: string;
  isMobile?: boolean;
  currency?: Currency;
  paymentSettingsNavigationConfig?: PaymentSettingsNavigationConfig;
  atlasBaseUrl?: string;
  breakpoint?: Breakpoint;
  userData?: UserData;
  compId?: string;
  isPreview?: boolean;
  isEditor?: boolean;
  isComposer?: boolean;
  isRTL?: boolean;
  enableAnimations?: boolean;
  baseUrl?: string;
  style?: IStyles;
}

export const DEFAULT_CONFIG: Config = {
  language: 'en',
  accountLanguage: 'en',
  regionalFormat: 'en',
};

type RequiredConfigKeys = 'language' | 'regionalFormat' | 'accountLanguage';

export type ConfigInput = Omit<Config, RequiredConfigKeys> &
  Partial<Pick<Config, RequiredConfigKeys>>;

export const ConfigContext = createContext<Config | null>(null);

export interface ConfigContextProviderProps {
  value: ConfigInput;
  children: ReactNode;
}

export function ConfigContextProvider({
  value: configInput,
  children,
}: ConfigContextProviderProps) {
  const config: Config = {
    ...configInput,
    language: configInput.language ?? DEFAULT_CONFIG.language,
    accountLanguage:
      configInput.accountLanguage ?? DEFAULT_CONFIG.accountLanguage,
    regionalFormat: configInput.regionalFormat ?? DEFAULT_CONFIG.regionalFormat,
  };
  return React.createElement(
    ConfigContext.Provider,
    { value: config },
    children,
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigContext.Provider');
  }

  return context || DEFAULT_CONFIG;
}
